import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import classNames from 'classnames';
import { BLUR_CONTENT_STYLE_NAME } from 'theme';
import { INearbyResort } from 'api/region/types';
import { EmptyBlock } from 'components/common/EmptyBlock';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import Grid from 'components/common/Grid/Grid';
import { RegularAd } from 'googlead/components/RegularAd';
import { HeaderClickEvent, IGridColumn, IGridSorting } from 'components/common/Grid/types';
import { fetchNearbyResorts } from 'api/home/static';
import { Diagnostics } from 'util/diagnostics';

import gridStyles from './components/styles.module.scss';
import styles from './styles.module.scss';
import { nearbyResortsGridColumns } from './components/gridColumns';

export interface IProps {
  resorts: INearbyResort[];
  isFresh: boolean;
  from: { latitude: number, longitude: number } | null;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
    * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
}
export const NearbyResortsBlock = ({ resorts, isFresh, from }: IProps) => {
  const { t } = useOTSTranslation('common');
  const columns = nearbyResortsGridColumns();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resortsList, setResortsList] = useState<Array<INearbyResort>>(resorts);
  const [gridSorting, setGridSorting] = useState<IGridSorting>();

  const onSortingChange = useCallback(async (event: HeaderClickEvent, column: IGridColumn, sorting?: IGridSorting) => {
    setIsLoading(true);
    // console.log(sorting, from, sorting);
    try {
      const response = await fetchNearbyResorts(
        from?.latitude,
        from?.longitude,
        sorting?.orderBy,
        sorting?.direction,
      );
      setResortsList(response);
      setGridSorting(sorting);
    } catch (e: any) {
      Diagnostics.error({
        tag: 'nearby-resorts',
        error: e,
        message: 'Failed to load nearby',
        data: { sorting },
      });
    }
    setIsLoading(false);
  }, [from]);

  useEffect(() => {
    setResortsList(resorts);
  }, [resorts]);

  const wDistance = useMemo(() => resortsList.map((resort) => ({
    ...resort,
    distance: from ? getDistanceFromLatLonInKm(resort.latitude, resort.longitude, from.latitude, from.longitude) : undefined,
  })), [resortsList]);

  if (isFresh && !isLoading && resorts.length === 0) {
    return (
      <EmptyBlock>
        {t('nearbyResorts.empty')}
      </EmptyBlock>
    );
  }

  return (
    <section className={classNames(styles.box, isFresh ? '' : BLUR_CONTENT_STYLE_NAME, resorts.length === 0 ? 'hidden' : '')}>
      <Grid
        defaultSorting={gridSorting}
        onSortingChange={onSortingChange}
        data={wDistance}
        total={wDistance.length}
        columns={columns}
        isLoading={!isFresh || isLoading}
        noValueSymbol={t('common.dash')}
        className={gridStyles.table}
      />
      <RegularAd />
    </section>
  );
};
